import * as React from 'react';
import { ApiHooksCaching } from './caching';
/**
 * API Hooks - Store
 * ----------------
 * Parsing of the generated API client into a set of hooks.
 *
 * This file contains everything relating to the store, includes:
 * - React context
 * - React reducer
 * - Updater component
 * - Library of actions
 */
export namespace ApiHooksStore {
  /** TYPES */

  /** Root type for the state object */
  export type State = { [endpointKey: string]: { [paramHash: string]: StateSlice<any> } };

  //* * Type for a test key to be used when called from a test file */
  export type TestKeyState = { [endpointKey: string]: { testKey: string } };

  /**
   * Type for a single slice of data
   * - specific to one endpoint and set of parameters
   * @param status The current status of the stored data
   * @param data The stored data
   * @param error An API error if caught - is reset after a successful request
   * @param paramHash A string representing the specific set of parameters passed to this data request
   * @param timestamp The UNIX timestamp of the last request
   */
  export interface StateSlice<TData> {
    status?: 'loading' | 'loaded' | 'error';
    data?: TData;
    error?: any;
    paramHash: string;
    timestamp?: number;
  }

  /** ACTIONS */

  export namespace Actions {
    /** TYPES */

    /**
     * Root type for a state update action sent from an API hook
     * @param endpointKey A key specific to the endpoint (in format `controller.endpoint`)
     * @param cacheKeyValue A key to cache the data by - each unique key will represent a different state slice in the dictionary.
     * @param paramHash A string representing the specific set of parameters passed to this data request
     * @param maxCachingDepth The maximum number of data sets to store for an endpoint - comes from a query config setting
     */
    export interface Action extends StateSlice<any> {
      endpointKey: string;
      cacheKeyValue: string;
      maxCachingDepth: number;
      paramHash: string;
    }

    /**
     * Root type for the data reset action
     * @param reset A constant 'true' - used by the reducer to detect a reset action
     * @param endpointKey (optional) Key of the endpoint to reset - resets all endpoints of not passed
     * @param cacheKeyValue (optional) A key to reset - each unique key will represent a different state slice in the dictionary.
     */
    export interface ResetAction {
      reset: true;
      endpointKey?: string;
      cacheKeyValue?: string;
    }

    /** ACTION FACTORIES */

    /**
     * Detects a reset action vs a state update action
     * @param action The action to check
     * @returns A boolean - true if reset action, false if state update action
     */
    export function isResetAction(
      action: React.ReducerAction<React.Reducer<State, Actions.Action | Actions.ResetAction>>
    ): action is ResetAction {
      return !!(action as ResetAction).reset;
    }

    /**
     * Factory function for creating a 'loading' state update action
     * @param endpointKey A key specific to the endpoint (in format `controller.endpoint`)
     * @param paramHash A string representing the specific set of parameters passed to this data request
     * @param cacheKeyValue A key to cache the data by - each unique key will represent a different state slice in the dictionary.
     * @param maxCachingDepth The maximum number of data sets to store for an endpoint - comes from a query config setting
     * @returns An action object to be dispatched
     */
    export function loading(
      endpointKey: string,
      paramHash: string,
      cacheKeyValue: string,
      maxCachingDepth: number
    ): React.ReducerAction<React.Reducer<State, Actions.Action | Actions.ResetAction>> {
      return { status: 'loading', endpointKey, cacheKeyValue, paramHash, maxCachingDepth };
    }

    /**
     * Factory function for creating a 'loaded' state update action
     * @param endpointKey A key specific to the endpoint (in format `controller.endpoint`)
     * @param paramHash A string representing the specific set of parameters passed to this data request
     * @param cacheKeyValue A key to cache the data by - each unique key will represent a different state slice in the dictionary.
     * @param data The data returned from the request
     * @param maxCachingDepth The maximum number of data sets to store for an endpoint - comes from a query config setting
     * @returns An action object to be dispatched
     */
    export function loaded<TData>(
      endpointKey: string,
      paramHash: string,
      cacheKeyValue: string,
      data: TData,
      maxCachingDepth: number
    ): React.ReducerAction<React.Reducer<State, Actions.Action | Actions.ResetAction>> {
      return {
        status: 'loaded',
        timestamp: Date.now(),
        endpointKey,
        cacheKeyValue,
        paramHash,
        data,
        maxCachingDepth,
        error: undefined,
      };
    }

    /**
     * Factory function for creating an 'error' state update action
     * @param endpointKey A key specific to the endpoint (in format `controller.endpoint`)
     * @param paramHash A string representing the specific set of parameters passed to this data request
     * @param cacheKeyValue A key to cache the data by - each unique key will represent a different state slice in the dictionary.
     * @param requestError The error returned from the request
     * @param maxCachingDepth The maximum number of data sets to store for an endpoint - comes from a query config setting
     * @returns An action object to be dispatched
     */
    export function error(
      endpointKey: string,
      paramHash: string,
      cacheKeyValue: string,
      requestError: any,
      maxCachingDepth: number
    ): React.ReducerAction<React.Reducer<State, Actions.Action | Actions.ResetAction>> {
      return { status: 'error', endpointKey, cacheKeyValue, paramHash, error: requestError, maxCachingDepth };
    }

    /**
     * Factory function for creating an 'reset' action
     * @param endpointKey (optional) Key of the endpoint to reset - resets all endpoints of not passed
     * @param cacheKeyValue (optional) A key to reset - each unique key will represent a different state slice in the dictionary.
     * @returns An action object to be dispatched
     */
    export function reset(
      endpointKey?: string,
      cacheKeyValue?: string
    ): React.ReducerAction<React.Reducer<State, Actions.Action | Actions.ResetAction>> {
      return { reset: true, endpointKey, cacheKeyValue };
    }
  }

  /**  STATE FLOW - context/reducer/updater */

  /**
   * API Hooks - React context
   * - Contains the state object and dispatch method
   * - All Api hooks listen for changes from this
   */
  export const Context = React.createContext<
    [
      State,
      React.Dispatch<React.ReducerAction<React.Reducer<State, Actions.Action | Actions.ResetAction>>>,
      TestKeyState
    ]
  >([{}, undefined, undefined]);

  /**
   * Api Hooks - React reducer
   * - The single reducer for all changes in state
   * - Listens to actions dispatched from the hooks and builds the single state object
   * @param state The current state object
   * @param action The incoming action
   */
  export const reducer: React.Reducer<State, Actions.Action | Actions.ResetAction> = (state, action) => {
    // check for reset action and reset the endpoint data
    if (Actions.isResetAction(action)) {
      const { endpointKey, cacheKeyValue } = action;
      if (endpointKey) {
        return {
          ...state,
          [endpointKey]: cacheKeyValue
            ? {
                ...(state[endpointKey] ?? {}),
                [cacheKeyValue]: undefined,
              }
            : undefined,
        };
      }
      return {};
    }
    // rebuild the state object by applying changes from the action to the current state
    const { endpointKey, cacheKeyValue, maxCachingDepth, ...stateSlice } = action;
    const newState = {
      ...state,
      [endpointKey]: {
        ...(state[endpointKey] ?? {}),
        [cacheKeyValue]: {
          ...(state[endpointKey]?.[cacheKeyValue] ?? {}),
          ...stateSlice,
        },
      },
    };
    // make sure the dictionary of state slices for this endpoint hasn't exceeded the maximum depth
    newState[endpointKey] = ApiHooksCaching.cleanEndpointDictionary(newState[endpointKey], maxCachingDepth);
    return newState;
  };

  /**
   * Api Hooks - React Provider
   * - Must be imported and wrapped around the entire app
   * - Enables re-renders downstream when state changes
   * @param param0 The props for the provider component (should only be children)
   */
  export const Provider: React.FC<{ testKeys?: TestKeyState }> = ({ children, testKeys }) => {
    // AREA 1

    const initialState = React.useMemo(() => {
      return JSON.parse(localStorage.getItem('my-cache') ?? '{}');
    }, []);

    const [state, dispatch] = React.useReducer(reducer, initialState);

    // AREA 2

    React.useEffect(() => {
      localStorage.setItem('my-cache', JSON.stringify(state));
    }, [state]);

    return <Context.Provider value={[state, dispatch, testKeys]}>{children}</Context.Provider>;
  };
}
